﻿import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { withRouter } from 'react-router';
import * as SurveyStateController from '../../../store/StateControllers/Survey/SurveyStateController';
import * as Models from '../../../store/Models/Surveys/SurveyDataModel';
import { SurveyProps } from '../CreateAndEditSurveys';
import { SidebarDetail } from '../../core/SidebarDetail';
import * as SelectSuppliersMaterialsStateController from '../../../store/StateControllers/Supplier/SelectSuppliersMaterialsTableStateController';
import CreateAndEditSurveyPreviewRenderer from './CreateAndEditSurveyPreviewRenderer';
import { Section } from '../../../config/Models';
import { StandardSummaryDataFieldsManager } from '../../../config/StandardSummaryDataFieldsManager';
import { RohsExemptions } from '../../../application-layer/compliance/RohsExemptions';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { TeamRole } from '../../../store/Models/UserContext';
import { DynamicSidebar } from '../../core/DynamicSidebar';
import { QuestionnairePage, QuestionnaireQuestion, QuestionnairePredefinedAnswer, QuestionType } from '../../../store/Models/Surveys/SurveyDataModel';
import Upsell, { UpsellTypes } from '../../Upsell/Upsell';
import { UpsellProBadge } from '../../core/Upsell';
import { RegulationManager } from '../../../store/Managers/RegulationManager';
import { DynamicQuestion } from '../../core/DynamicQuestion';
import { RegulationDependentBomColumnsManager } from '../../../store/Managers/RegulationDependentBomColumnsManager';
import CheckBox from '../../core/CheckBox';

import "./../styles.css";

interface CreateAndEditSurveyQuestionsPageState {
    currentQuestionnairePage: number;
    currentQuestionnaireQuestion: number;
    currentQuestionnaireAnswer: number;
    selectableQuestionnairePages: QuestionnairePage[];
    selectableQuestionnaireQuestions: QuestionnaireQuestion[];
    selectableQuestionnaireAnswers: QuestionnairePredefinedAnswer[];
    disabledRegulationDependentBomColumns: string[]
}

interface LocalProps {
    isEmbedded: boolean,
    snapshotSurveyState?: Models.SurveyState,
    setRegulationChangedSideEffect?: (cb: (regulationId: string, regulationChecked: boolean) => void) => void
}

type ComponentProps = SurveyProps & LocalProps;

class CreateAndEditSurveyQuestionsPage extends ToxnotSignInRequiredComponent<ComponentProps, CreateAndEditSurveyQuestionsPageState>{
    // ===============================================================================

    private getFilteredRegulationsToRender(regulationsToRender: any[]) {
        if (!this.isSurveyEditable()) {
            return regulationsToRender;
        }
        return regulationsToRender.filter(
            obj =>
                obj.regulationId !== RegulationManager.ConflictMineralsRegulationId &&
                obj.regulationId !== RegulationManager.ConflictMineralsTemplateRegulationId
        );
    }

    private regulationArrayToIdNameMap(regulations: any[]): { [regulationId: string]: string } {
        return regulations.reduce((table: { [regulationId: string]: string }, reg: any) => {
            table[reg.regulationId] = reg.config.displayName?.length > 0 ? reg.config.displayName : reg.config.name;
            return table;
        }, {});
    }

    //private readonly regulationKeys;
    private readonly allowedRequiredQuestionTypes;
    private regulationsToRender = RegulationManager.getRegulationsAs2GroupsAlphabatized(this.props.surveyMetadata.dynamicRegulations);

    public constructor(props: ComponentProps | Readonly<ComponentProps>) {
        super(props);

        this.allowedRequiredQuestionTypes = ["multiple", "multi-checkbox", "multiple-dropdown"];
        this.setSavedQuestionnaireDetails(this.props.surveyRequest.bomQuestionnaireId);

        // this will make the regulationChangedSideEffects method accessible to the parent component
        if (this.props.setRegulationChangedSideEffect !== undefined) {
            this.props.setRegulationChangedSideEffect(this.regulationChangedSideEffects);
        }
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }

    // ===============================================================================

    private shouldShowCustomQuestionsUpsell = () =>
        !this.getUserContextForSignedInUser().isSubscriptionActive && !this.props.surveyRequest.dataRequested.customQuestionsAsked;

    // ===============================================================================

    protected renderConcreteComponent() {
        const complianceValueToFriendlyNameMap = Models.SurveyComplianceRegulationValues.getLocalizedFriendlyDescriptionMap(this.regulationsToRender);
        const getLocalizedDisplayNameMap = Models.SurveyComplianceRegulationValues.getLocalizedDisplayNameMap(this.regulationsToRender);

        const getSectionActiveClass = (section: Models.DisplaySection) => {
            return this.props.surveyUi.currentSection === section
                ? "active"
                : "";
        }

        return (
            <div>
                <div className={this.props.isEmbedded ? "" : "tn-section-row"}>
                    <div className={this.props.isEmbedded ? "" : "tn-section-col-12"}>
                        {this.props.surveyUi.surveyHasRequestsThatAreRedundantWithCustomQuestionnaire &&
                            <div className="alert alert-warning">
                                {this.localizeString("Your custom questionnaire has pages that are redundant with the data you are asking for in the survey (e.g. compliance). Please update your questionnaire.")}
                            </div>
                        }
                        <div className="grouped-data">
                            <div className="group">
                                {!this.props.isEmbedded &&
                                    <div className="title">{this.localizeString("Request Data from your Supplier")}</div>
                                }
                                <div className={`content ${this.props.isEmbedded ? "p-l-0-imp m-t-15" : ""}`}>
                                    <div id="data-requests-root">
                                        <div className="sections-container">
                                            <p>
                                                <label>{this.localizeString("Select data that you want to ask for:")}</label>
                                            </p>
                                            {(this.props.surveyRequest.bomType !== 2) && (
                                                <React.Fragment>
                                                    <div className={`section ${getSectionActiveClass(Models.DisplaySection.MaterialDisclosure)}`} onClick={e => this.changeSection(Models.DisplaySection.MaterialDisclosure)}>
                                                        <div className="title">{this.renderQuestionIcon(this.props.surveyRequest.dataRequested.materialDisclosureAsked)}<div className="title-text">{this.localizeString("Material Disclosure")}</div></div>
                                                        <div className="content">
                                                            {this.props.surveyRequest.dataRequested.materialDisclosureAsked &&
                                                                <React.Fragment>
                                                                    {this.props.surveyRequest.sdsRequested && <p>{this.localizeString("SDS File Required")}</p>}
                                                                    {(this.props.surveyRequest.questionnaireAnswerThatMakesBomDataRequired !== null) && <p>{this.localizeString("Dependent on Questionnaire Answer")}</p>}
                                                                </React.Fragment>
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className={`section ${getSectionActiveClass(Models.DisplaySection.ComplianceData)}`} onClick={e => this.changeSection(Models.DisplaySection.ComplianceData)}>
                                                        <div className="title">{this.renderQuestionIcon(this.props.surveyRequest.dataRequested.complianceDataAsked ?? false)}<div className="title-text">{this.localizeString("Compliance")}</div></div>
                                                        <div className="content">
                                                            {this.props.surveyRequest.dataRequested.complianceDataAsked &&
                                                                this.props.surveyRequest.dataRequested.complianceList.sort((firstObject, secondObject) => (complianceValueToFriendlyNameMap[firstObject] ?? firstObject).localeCompare((complianceValueToFriendlyNameMap[secondObject] ?? secondObject))).map((v, i) => <p key={i}>{getLocalizedDisplayNameMap[v] ?? v}</p>)
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className={`section ${getSectionActiveClass(Models.DisplaySection.SustainabilityData)}`} onClick={e => this.changeSection(Models.DisplaySection.SustainabilityData)}>
                                                        <div className="title">{this.renderQuestionIcon(this.props.surveyRequest.dataRequested.sustainabilityDataAsked)}<div className="title-text">{this.localizeString("Lifecycle Impacts")}</div></div>
                                                        <div className="content">
                                                            {this.props.surveyRequest.dataRequested.sustainabilityDataAsked &&
                                                                <React.Fragment>
                                                                    {this.props.surveyRequest.dataRequested.embodiedCarbonDataAsked && <p>{this.localizeString("Embodied Carbon")}</p>}
                                                                    {this.props.surveyRequest.dataRequested.waterUseDataAsked && <p>{this.localizeString("Water Use")}</p>}
                                                                    {this.props.surveyRequest.dataRequested.carbonTransparencyDataAsked && <p>{this.localizeString("Carbon Transparency")}</p>}
                                                                    {this.props.surveyRequest.dataRequested.carbonOtherDataAsked && <p>{this.localizeString("Carbon Assessment, Commitments, and Optimization")}</p>}
                                                                </React.Fragment>
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className={`section ${getSectionActiveClass(Models.DisplaySection.VocData)}`} onClick={e => this.changeSection(Models.DisplaySection.VocData)}>
                                                        <div className="title">{this.renderQuestionIcon(this.props.surveyRequest.dataRequested.vocDataAsked)}<div className="title-text">{this.localizeString("VOC data")}</div></div>
                                                        <div className="content">
                                                            {this.props.surveyRequest.dataRequested.vocDataAsked &&
                                                                <React.Fragment>
                                                                    {this.props.surveyRequest.dataRequested.vocTransparencyDataAsked && <p>{this.localizeString("VOC Transparency")}</p>}
                                                                    {this.props.surveyRequest.dataRequested.vocOtherDataAsked && <p>{this.localizeString("VOC Assessment, Commitments, and Optimization")}</p>}
                                                                </React.Fragment>
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className={`section ${getSectionActiveClass(Models.DisplaySection.CircularityData)}`} onClick={e => this.changeSection(Models.DisplaySection.CircularityData)}>
                                                        <div className="title">{this.renderQuestionIcon(this.props.surveyRequest.dataRequested.circularityDataAsked)}<div className="title-text">{this.localizeString("Circularity")}</div></div>
                                                        <div className="content"></div>
                                                    </div>
                                                    <div className={`section ${getSectionActiveClass(Models.DisplaySection.DocumentsCertifications)}`} onClick={e => this.changeSection(Models.DisplaySection.DocumentsCertifications)}>
                                                        <div className="title">{this.renderQuestionIcon(this.props.surveyRequest.dataRequested.documentsAsked ?? false)}<div className="title-text">{this.localizeString("Additional Documents & Certifications")}</div></div>
                                                        <div className="content">
                                                            {this.props.surveyRequest.dataRequested.documentsAsked &&
                                                                <p>{this.props.surveyRequest.dataRequested.documentsCertificationList.map(v => v.documentCertificationType === "Other" && v.documentNameIfOther?.length > 0 ? v.documentNameIfOther : v.documentCertificationType).join(", ")}</p>
                                                            }
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            )}
                                            <div className={`section ${getSectionActiveClass(Models.DisplaySection.CustomQuestions)}`} onClick={e => this.changeSection(Models.DisplaySection.CustomQuestions)}>
                                                <div className="title">{this.renderQuestionIcon(this.props.surveyRequest.dataRequested.customQuestionsAsked ?? false)}
                                                    <div className="title-text">{this.localizeString("Custom Questionnaires")}</div>
                                                    {this.shouldShowCustomQuestionsUpsell() && <div><UpsellProBadge /></div>}
                                                </div>
                                                <div className="content">
                                                    {this.props.surveyRequest.dataRequested.customQuestionsAsked &&
                                                        <React.Fragment>
                                                            {this.props.surveyRequest.bomQuestionnaireId !== null && <p>{this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.bomQuestionnaireId]?.name?.length > 0 ? this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.bomQuestionnaireId]?.name : "Unnamed Questionnaire"}{this.props.surveyRequest.preFillBomQuestionnaireFromSummaryData ? " " + this.localizeString("Prefilled") : ""}</p>}
                                                            {this.props.surveyRequest.supplierQuestionnaireId !== null && <p>{this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.supplierQuestionnaireId]?.name?.length > 0 ? this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.supplierQuestionnaireId]?.name : "Unnamed Questionnaire"}{this.props.surveyRequest.preFillSupplierQuestionnaireFromSummaryData ? " " + this.localizeString("Prefilled") : ""}</p>}
                                                            {this.props.surveyRequest.attestationQuestionnaireId !== null && <p>{this.localizeString("Attestation Questionnaire")}</p>}
                                                        </React.Fragment>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className="section-options-body">
                                            {this.renderSideSectionOptionsBody(this.props.surveyUi.currentSection)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private setSavedQuestionnaireDetails(bomQuestionnaireId: string | null) {
        let selectableQuestionnairePages = new Array<QuestionnairePage>();
        let currentQuestionnairePage = 0;
        let selectableQuestionnaireQuestions = new Array<QuestionnaireQuestion>();
        let currentQuestionnaireQuestion = 0;
        let selectableQuestionnaireAnswers = new Array<QuestionnairePredefinedAnswer>();
        let currentQuestionnaireAnswer = 0;

        if (bomQuestionnaireId) {

            selectableQuestionnairePages = this.props.surveyMetadata.questionnaireTable[bomQuestionnaireId]?.pages?.filter(p => p?.questions?.some(q => this.allowedRequiredQuestionTypes.includes(q.type)));
            const selectedPage = selectableQuestionnairePages.firstOrDefault(p => p.title === this.props.surveyRequest.questionnaireAnswerThatMakesBomDataRequired?.pageTitle)
            if (selectedPage) {
                currentQuestionnairePage = selectableQuestionnairePages.indexOf(selectedPage)
                selectableQuestionnaireQuestions = selectedPage.questions.filter(q => this.allowedRequiredQuestionTypes.includes(q.type))
                const selectedQuestion = selectableQuestionnaireQuestions.firstOrDefault(q => q.text === this.props.surveyRequest.questionnaireAnswerThatMakesBomDataRequired?.questionText)
                if (selectedQuestion) {
                    currentQuestionnaireQuestion = selectableQuestionnaireQuestions.indexOf(selectedQuestion);
                    selectableQuestionnaireAnswers = selectedQuestion.predefinedAnswers;
                    const selectedResponse = selectableQuestionnaireAnswers.firstOrDefault(r => r.nativeAnswer === this.props.surveyRequest.questionnaireAnswerThatMakesBomDataRequired?.nativeAnswer)
                    if (selectedResponse) {
                        currentQuestionnaireAnswer = selectableQuestionnaireAnswers.indexOf(selectedResponse);
                    }
                }
            }


        }
        this.state = {
            selectableQuestionnairePages: selectableQuestionnairePages,
            currentQuestionnairePage: currentQuestionnairePage,
            selectableQuestionnaireQuestions: selectableQuestionnaireQuestions,
            currentQuestionnaireQuestion: currentQuestionnaireQuestion,
            selectableQuestionnaireAnswers: selectableQuestionnaireAnswers,
            currentQuestionnaireAnswer: currentQuestionnaireAnswer,
            disabledRegulationDependentBomColumns: this.getDisabledRegulationDependentBomColumns()
        };
    }


    private async setNewQuestionnaireDetails(bomQuestionnaireId: string | null, currentPage: number, currentQuestion: number, currentResponse: number) {
        let selectableQuestionnairePages = new Array<QuestionnairePage>();
        let currentQuestionnairePage = 0;
        let selectableQuestionnaireQuestions = new Array<QuestionnaireQuestion>();
        let currentQuestionnaireQuestion = 0;
        let selectableQuestionnaireAnswers = new Array<QuestionnairePredefinedAnswer>();
        let currentQuestionnaireAnswer = 0;

        if (bomQuestionnaireId) {
            currentQuestionnairePage = currentPage;
            currentQuestionnaireAnswer = currentResponse;
            currentQuestionnaireQuestion = currentQuestion;

            selectableQuestionnairePages = this.props.surveyMetadata.questionnaireTable[bomQuestionnaireId]?.pages?.filter(p => p?.questions?.some(q => this.allowedRequiredQuestionTypes.includes(q.type)));

            const selectedPage = selectableQuestionnairePages[currentPage]
            if (selectedPage) {
                selectableQuestionnaireQuestions = selectedPage.questions.filter(q => this.allowedRequiredQuestionTypes.includes(q.type));
                const selectedQuestion = selectableQuestionnaireQuestions[currentQuestion]
                if (selectedQuestion) {
                    selectableQuestionnaireAnswers = selectedQuestion.predefinedAnswers;
                }
            }
        }

        await this.setState({
            selectableQuestionnairePages: selectableQuestionnairePages, currentQuestionnairePage: currentQuestionnairePage, selectableQuestionnaireQuestions: selectableQuestionnaireQuestions,
            currentQuestionnaireQuestion: currentQuestionnaireQuestion, selectableQuestionnaireAnswers: selectableQuestionnaireAnswers, currentQuestionnaireAnswer: currentQuestionnaireAnswer
        })
    }

    // ===============================================================================

    private renderQuestionIcon(condition: boolean) {
        return (
            <div className={"section-status " + (condition ? "enabled" : "disabled")}>
                {condition && <span className="glyphicon glyphicon-ok" aria-hidden="true"></span>}
            </div>
        );
    }

    // ===============================================================================

    private changeSection(type: Models.DisplaySection) {
        this.props.setSurveyUIData({ currentSection: type });
    }

    // ===============================================================================

    private alertInvalidIngredientsTableSetup() {
        alert(this.localizeString("Sorry, you can't request both Material Disclosure and a Custom Questionnaire with an ingredient table. You can't ask for Material Disclosure if you're asking for Ingredient Tables in your Questionnaire."));
    }

    // ===============================================================================

    private toggleQuestion(type: Models.DisplaySection) {
        let key = "";
        let currentValue: boolean | undefined;
        let disabledRegulationDependentBomColumns: string[] = [];

        switch (type) {
            case Models.DisplaySection.MaterialDisclosure:
                key = "materialDisclosureAsked";
                currentValue = this.props.surveyRequest.dataRequested.materialDisclosureAsked;
                if (currentValue) {
                    // if we are removing material disclosure, the requestor cannot require the supplier to
                    // add requlatory ingredients.
                    this.props.setSurveyRequestData({
                        sdsRequested: false,
                        metadata: {
                            ...this.props.surveyRequest.metadata,
                            requireSuppliersToDiscloseRegulatoryIngredients: false
                        }
                    });

                    this.props.surveyRequest.dataRequested.complianceList.map(reg => {
                        this.regulationChangedSideEffects(reg, false);
                    });
                } else if (this.props.surveyRequest.dataRequested.complianceDataAsked) {
                    disabledRegulationDependentBomColumns = this.getDisabledRegulationDependentBomColumns();
                }
                break;
            case Models.DisplaySection.ComplianceData:
                key = "complianceDataAsked";
                currentValue = this.props.surveyRequest.dataRequested.complianceDataAsked;
                if (currentValue) {
                    this.props.surveyRequest.dataRequested.complianceList.map(reg => {
                        this.regulationChangedSideEffects(reg, false);
                    });
                } else if (this.props.surveyRequest.dataRequested.materialDisclosureAsked) {
                    disabledRegulationDependentBomColumns = this.getDisabledRegulationDependentBomColumns();
                }
                break;
            case Models.DisplaySection.SustainabilityData:
                key = "sustainabilityDataAsked";
                currentValue = this.props.surveyRequest.dataRequested.sustainabilityDataAsked;
                if (currentValue) {
                    this.props.surveyRequest.dataRequested.carbonOtherDataAsked = false;
                    this.props.surveyRequest.dataRequested.carbonTransparencyDataAsked = false;
                    this.props.surveyRequest.dataRequested.waterUseDataAsked = false;
                    this.props.surveyRequest.dataRequested.embodiedCarbonDataAsked = false;
                }
                break;
            case Models.DisplaySection.CircularityData:
                key = "circularityDataAsked";
                currentValue = this.props.surveyRequest.dataRequested.circularityDataAsked;
                break;
            case Models.DisplaySection.CustomQuestions:
                key = "customQuestionsAsked";
                currentValue = this.props.surveyRequest.dataRequested.customQuestionsAsked;
                if (currentValue) {
                    this.props.surveyRequest.bomQuestionnaireId = null;
                    this.props.surveyRequest.attestationQuestionnaireId = null;
                    this.props.surveyRequest.supplierQuestionnaireId = null;
                    this.props.surveyRequest.preFillBomQuestionnaireFromSummaryData = false;
                    this.props.surveyRequest.preFillSupplierQuestionnaireFromSummaryData = false;
                    this.props.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer = false;
                    this.props.surveyRequest.questionnaireAnswerThatMakesBomDataRequired = null;
                }
                break;
            case Models.DisplaySection.DocumentsCertifications:
                key = "documentsAsked";
                currentValue = this.props.surveyRequest.dataRequested.documentsAsked;
                if (currentValue) {
                    this.props.surveyRequest.dataRequested.documentsCertificationList = [];
                }
                break;
            case Models.DisplaySection.VocData:
                key = "vocDataAsked";
                currentValue = this.props.surveyRequest.dataRequested.vocDataAsked;
                if (currentValue) {
                    this.props.surveyRequest.dataRequested.vocOtherDataAsked = false;
                    this.props.surveyRequest.dataRequested.vocTransparencyDataAsked = false;
                }
                break;
        }

        //
        // Don't allow the user to include both a questionnaire with ingredients tables &
        // also ask for material disclosure (bom data).
        //

        if (!currentValue && (this.props.surveyRequest.bomQuestionnaireId !== null)) {
            const questionnaire = this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.bomQuestionnaireId];

            const questionnaireHasIngredientsTable = questionnaire?.pages.selectMany(page => page.questions).some(question => question.type === "ingredients-table");

            const invalid = questionnaireHasIngredientsTable &&
                (((type === Models.DisplaySection.MaterialDisclosure) && this.props.surveyRequest.dataRequested.customQuestionsAsked) ||
                    ((type === Models.DisplaySection.CustomQuestions) && (this.props.surveyRequest.dataRequested.materialDisclosureAsked || this.props.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer)));

            if (invalid) {
                this.alertInvalidIngredientsTableSetup();
                return;
            }
        }

        if (key !== "") {
            if (type === Models.DisplaySection.MaterialDisclosure || type === Models.DisplaySection.ComplianceData) {
                this.setState({ disabledRegulationDependentBomColumns: disabledRegulationDependentBomColumns });
            }
            this.props.setDataRequested({ [key]: !currentValue });
        }
    }

    // ===============================================================================

    private async setQuestionnaireAnswerThatMakesBomDataRequired(pageIndex: number, questionIndex: number, predefinedAnswerIndex: number, currentBomQuestionnaireId?: string) {
        const bomQuestionnaireId = currentBomQuestionnaireId ?? this.props.surveyRequest.bomQuestionnaireId;
        await this.setNewQuestionnaireDetails(bomQuestionnaireId, pageIndex, questionIndex, predefinedAnswerIndex);

        if (bomQuestionnaireId !== null) {
            const data: Models.QuestionnaireAnswerThatMakesBomDataRequiredSetting = {
                pageTitle: this.state.selectableQuestionnairePages?.[pageIndex]?.title ?? "",
                questionText: this.state.selectableQuestionnaireQuestions?.[questionIndex]?.text ?? "",
                nativeAnswer: this.state.selectableQuestionnaireAnswers?.[predefinedAnswerIndex]?.nativeAnswer ?? ""
            };
            this.props.setSurveyRequestData({ questionnaireAnswerThatMakesBomDataRequired: data });
        }
    }

    // ===============================================================================

    private shouldBomColumnCheckboxBeDisabled(surveyableColumn: Models.SurveyableColumn) {
        if ((!this.isSurveyEditable() || !surveyableColumn.canRemove)) {
            return true;
        }

        const linkedMaterialsExists = SurveyStateController.doLinkedMaterialsExist(this.props);

        const additionalDisableCheckboxCondition = (this.props.snapshotSurveyState?.surveyRequest.fields.firstOrDefault(surveyBomColumn => surveyBomColumn.columnId === surveyableColumn.columnId) ?? null) !== null;

        return linkedMaterialsExists || additionalDisableCheckboxCondition;
    }

    // ===============================================================================

    private toggleBomColumn(surveyableColumn: Models.SurveyableColumn, columnIsRequestedInSurvey: boolean) {
        this.props.toggleBomColumn(surveyableColumn, columnIsRequestedInSurvey);

        this.props.setSurveyRequestData({
            metadata: {
                ...this.props.surveyRequest.metadata,
            }
        });
    }

    // ===============================================================================

    private renderMaterialDisclosureBody() {
        const linkedMaterialsExists = SurveyStateController.doLinkedMaterialsExist(this.props);

        const surveyableColumnsForPicker = SurveyStateController.getSurveyableColumnsForPicker(this.props.surveyMetadata.surveyableColumns);

        const anyAdvancedColumnsSelected = surveyableColumnsForPicker.some(c => c.isAdvancedColumn && this.props.surveyRequest.fields.some(f => f.columnId === c.columnId));

        // EXG-7722 (M2 / Jira 1499213): Surface any persisted draft fields whose custom
        // BOM column can no longer be resolved by ColumnName on the current template so
        // the requestor can remove or replace them before save/send. Server sets
        // isUnavailable=true on these fields via NormalizeForDraftLoad.
        const unavailableFields = this.props.surveyRequest.fields.filter(f => f.isUnavailable);

        return (
            <div className="section-options-body-content">
                {linkedMaterialsExists &&
                    <div className="alert alert-warning" role="alert">
                        <p>{this.localizeString("Some of the selected products/materials contain linked materials. 3E Exchange can't ask for Material Disclosure for linked material.")}</p>
                    </div>}
                {unavailableFields.length > 0 &&
                    <div className="alert alert-warning" role="alert">
                        <p><strong>{this.localizeString("Some custom BOM columns in this draft are no longer available.")}</strong></p>
                        <p>{this.localizeString("The following custom columns were deleted, renamed, or changed type after the draft was saved. Please remove them or reselect the intended column before sending:")}</p>
                        <ul>
                            {unavailableFields.map((f, i) => (
                                <li key={`unavailable-${i}`}>
                                    <span>{f.columnName ?? f.columnId}</span>
                                    {" "}
                                    <button type="button" className="btn btn-xs btn-default" disabled={!this.isSurveyEditable()} onClick={e => this.toggleBomColumn({ columnId: f.columnId, pairedColumnId: null } as Models.SurveyableColumn, true)}>
                                        {this.localizeString("Remove")}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>}
                <div className="alert alert-info" role="alert">
                    <div className="title">{this.localizeString("Material Disclosure")}</div>
                    <div className="instructions">{this.localizeString("Ask your supplier for material ingredient & Ingredient Disclosure information - e.g. CAS numbers, Product % by Weight, etc.")}</div>
                    <div className="controls">
                        <div className="form-group">
                            <table id="column-selection-table" className="table tn-table">
                                <thead>
                                    <tr>
                                        <td>{this.localizeString("Column")}</td>
                                        <td>{this.localizeString("Required?")}</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {surveyableColumnsForPicker.map(
                                        (surveyableColumn, index) => {
                                            const surveyRequestColumn = this.props.surveyRequest.fields.firstOrDefault(surveyBomColumn => surveyBomColumn.columnId === surveyableColumn.columnId);
                                            const columnIsRequestedInSurvey = surveyRequestColumn !== null;
                                            const isPaired = surveyableColumn.pairedColumnId != null;
                                            const friendlyColumnName = isPaired
                                                ? this.getPairedSurveyableColumnIdsToFriendlyName(surveyableColumn.columnId)
                                                : (surveyableColumn.isCustomColumn
                                                    ? (surveyableColumn.columnName ?? this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap[surveyableColumn.columnId])
                                                    : this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap[surveyableColumn.columnId]);
                                            const isRequiredAtAll = (surveyRequestColumn !== null) &&
                                                (surveyRequestColumn.requiredInSummaryRow || surveyRequestColumn.requiredInNonSummaryNonLeafRows || surveyRequestColumn.requiredInLeafRows);

                                            if (!friendlyColumnName) {
                                                return null;
                                            }

                                            if (!anyAdvancedColumnsSelected && !this.props.surveyUi.showAdvancedIngredientColumns && surveyableColumn.isAdvancedColumn) {
                                                return null;
                                            }

                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="checkbox">
                                                            <label>
                                                                <input type="checkbox" checked={columnIsRequestedInSurvey} onChange={e => this.toggleBomColumn(surveyableColumn, columnIsRequestedInSurvey)} disabled={this.state.disabledRegulationDependentBomColumns.includes(surveyableColumn.columnId) || this.shouldBomColumnCheckboxBeDisabled(surveyableColumn)} /> {friendlyColumnName}
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {surveyRequestColumn !== null &&
                                                            <div className="btn-group">
                                                                <button type="button" className="btn btn-default btn-xs bom-column-required-toggle-button" disabled={!this.isSurveyEditable()} onClick={e => this.bomColumnRequiredToggleButton_click(surveyRequestColumn, surveyableColumn.pairedColumnId)}>
                                                                    {isRequiredAtAll
                                                                        ? this.localizeString("Required")
                                                                        : this.localizeString("Optional")
                                                                    }
                                                                </button>
                                                                <button type="button" className="btn btn-default btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" disabled={!this.isSurveyEditable()}>
                                                                    <span className="caret"></span><span className="sr-only">{this.localizeString("Toggle Dropdown")}</span>
                                                                </button>
                                                                <ul className="dropdown-menu dropdown-menu-right">
                                                                    <li><a role="button" onClick={e => this.bomColumnSummaryRowRequiredToggleButton_click(surveyRequestColumn, surveyableColumn.pairedColumnId)}>{this.localizeString("Required in Summary Row")} {surveyRequestColumn.requiredInSummaryRow && <span className="glyphicon glyphicon-ok"></span>}</a></li>
                                                                    <li><a role="button" onClick={e => this.bomColumnNonLeafRowsRequiredToggleButton_click(surveyRequestColumn, surveyableColumn.pairedColumnId)}>{this.localizeString("Required in all rows except lowest-level rows")} {surveyRequestColumn.requiredInNonSummaryNonLeafRows && <span className="glyphicon glyphicon-ok"></span>}</a></li>
                                                                    <li><a role="button" onClick={e => this.bomColumnLeafRowsRequiredToggleButton_click(surveyRequestColumn, surveyableColumn.pairedColumnId)}>{this.localizeString("Required in lowest-level rows")} {surveyRequestColumn.requiredInLeafRows && <span className="glyphicon glyphicon-ok"></span>}</a></li>
                                                                </ul>
                                                            </div>
                                                        }
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )}
                                </tbody>
                            </table>
                            {<div style={{ paddingLeft: "10px", marginBottom: "15px" }}><button type="button" className="btn btn-default btn-sm" onClick={e => this.props.setSurveyUIData({ showAdvancedIngredientColumns: !this.props.surveyUi.showAdvancedIngredientColumns })}>{(!anyAdvancedColumnsSelected && !this.props.surveyUi.showAdvancedIngredientColumns) ? this.localizeString("Advanced Columns") : this.localizeString("Hide Advanced Columns")}</button></div>}
                            <div className="instructions">{this.localizeString("SDS Options")}</div>
                            <div className="checkbox">
                                <label>
                                    <input type="checkbox" checked={this.props.surveyRequest.dataRequested.materialDisclosureAsked && this.props.surveyRequest.sdsRequested} onChange={e => this.props.setSurveyRequestData({ sdsRequested: !this.props.surveyRequest.sdsRequested })} disabled={!this.isSurveyEditable() || !this.props.surveyRequest.dataRequested.materialDisclosureAsked || linkedMaterialsExists || this.props.snapshotSurveyState?.surveyRequest.sdsRequested} /> {this.localizeString("Ask supplier to submit a SDS file.")}
                                </label>
                            </div>
                            {this.props.appServices.userContext?.isEditor && <button disabled={this.props.snapshotSurveyState?.surveyRequest.dataRequested.materialDisclosureAsked} role="button" className={`btn ${(this.props.surveyRequest.dataRequested.materialDisclosureAsked ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.MaterialDisclosure)}>{this.props.surveyRequest.dataRequested.materialDisclosureAsked ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>}
                        </div>
                    </div>
                </div>
                <div className="alert alert-example-fields">
                    <div className="title">{this.localizeString("Preview - Ingredient Disclosure Data")}</div>
                    <div className="controls">
                        <div className="example-bom-wrapper">
                            <div className="example-bom">
                                <div className="example-bom-row">
                                    {this.props.surveyRequest.fields.map((v, i) => {
                                        const previewFriendlyName = v.columnName
                                            ?? this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap[v.columnId]
                                            ?? "";
                                        return (
                                            <div key={i} className="example-bom-column">{this.localizeString(previewFriendlyName.coalesceToMaxSizeAndIndicateTruncationWithEllipsis(15))}</div>
                                        );
                                    })}
                                </div>
                                <div className="example-bom-row">
                                    {this.props.surveyRequest.fields.map((v, i) =>
                                        <div key={i} className="example-bom-column">{" "}</div>
                                    )}
                                </div>
                                <div className="example-bom-row">
                                    {this.props.surveyRequest.fields.map((v, i) =>
                                        <div key={i} className="example-bom-column">{" "}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderCal65ComplianceForm() {
        var applicabilityOption = this.localizeString("Regulation is applicable to this product/material");
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    <DynamicQuestion
                        questionText={this.localizeString("Does this product or packaging contain any chemicals listed on the California Proposition 65 substances list?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does this product require a Proposition 65 warning?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("If a warning is required provide warning text:")}
                        type={QuestionType.Comment}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("If a warning is not required provide the reason:")}
                        type={QuestionType.MultipleDropdown}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Please provide any documentation you have to back up these claims.")}
                        type={QuestionType.File}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Supplier Comments")}
                        type={QuestionType.Comment}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                {this.renderDefaultQuestions(applicabilityOption, applicabilityOption, this.localizeString("This reference date indicates when your response was determined."))}
            </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsReportingTemplate() {
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    {this.renderConflictMineralsCompanyInformation()}
                    {this.renderConflictMineralsComplianceForm()}
                    {this.renderConflictMineralsCompanyQuestions()}
                    {this.renderConflictMineralsSmelterList()}
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsV2ComplianceForm() {
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    {this.renderConflictMineralsCompanyInformationV2()}
                    <br></br>
                    {this.renderConflictMineralsComplianceFormV2()}
                    {this.renderConflictMineralsCompanyQuestionsV2()}
                    {this.renderConflictMineralsSmelterListV2()}
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsCompanyInformationV2() {
        return (
            <div>
                <h4><strong>{this.localizeString("Company Information")}</strong></h4>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Company Name")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Declaration of Scope or Class")}
                        type={QuestionType.MultipleDropdown}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Description of Scope")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Company Unique ID")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Company Unique ID Authority")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Address")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Contact Name")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Email - Contact")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div className="row" style={{ paddingBottom: "20px" }}>
                    <div className="col-sm-12">
                        <DynamicQuestion
                            questionText={this.localizeString("Phone - Contact")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Authorizer")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Title - Authorizer")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Email - Authorizer")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div className="row" style={{ paddingBottom: "20px" }}>
                    <div className="col-sm-12">
                        <DynamicQuestion
                            questionText={this.localizeString("Phone - Authorizer")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Effective Date")}
                        type={QuestionType.Datetime}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsCompanyInformation() {
        return (
            <div>
                <h4><strong>Company Information</strong></h4>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Company Name")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Declaration of Scope or Class")}
                        type={QuestionType.MultipleDropdown}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Description of Scope")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Company Unique ID")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Company Unique ID Authority")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Address")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Contact Name")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Email - Contact")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div className="email mb-5">
                    <label>Phone - Contact</label>
                </div>
                <div className="row" style={{ paddingBottom: "20px" }}>
                    <div className="col-sm-4">
                        <DynamicQuestion
                            questionText={this.localizeString("Country")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                    <div className="col-sm-4">
                        <DynamicQuestion
                            questionText={this.localizeString("Number")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                    <div className="col-sm-4">
                        <DynamicQuestion
                            questionText={this.localizeString("Ext")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Authorizer")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Title")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Email")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

                <div className="email mb-5">
                    <label>Phone</label>
                </div>
                <div className="row" style={{ paddingBottom: "20px" }}>
                    <div className="col-sm-4">
                        <DynamicQuestion
                            questionText={this.localizeString("Country")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                    <div className="col-sm-4">
                        <DynamicQuestion
                            questionText={this.localizeString("Number")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                    <div className="col-sm-4">
                        <DynamicQuestion
                            questionText={this.localizeString("Ext")}
                            type={QuestionType.Text}
                            answerOptions={[]}
                            appServices={this.props.appServices}>
                        </DynamicQuestion>
                    </div>
                </div>
                <div style={{ paddingBottom: "20px" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Effective Date")}
                        type={QuestionType.Datetime}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsComplianceForm() {
        var applicabilityOption = this.localizeString("Regulation is applicable to this product/material")
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    <DynamicQuestion
                        questionText={this.localizeString("Is any 3TG intentionally added or used in the material or in the production processes?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No"), this.localizeString("Unknown")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does any 3TG remain in the material?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Do any of the smelters in your supply chain source the 3TG from the covered countries?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does 100% of the 3TG (necessary to the functionality or production of the material) originate from recycled or scrap sources?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("What percentage of relevant suppliers have posted a response to your supply chain survey?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("100%"), this.localizeString("> 90%"), this.localizeString("> 75%"), this.localizeString("> 50%"), this.localizeString("< 50%"), this.localizeString("None")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Have you identified all of the smelters supplying the 3TG to your supply chain?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Please attach the CMRT form")}
                        type={QuestionType.File}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                {this.renderDefaultQuestions(applicabilityOption, applicabilityOption, this.localizeString("This reference date indicates when your response was determined."))}
            </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsComplianceFormV2() {
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data" style={{ whiteSpace: "nowrap" }}>
                    <DynamicQuestion
                        questionText={this.localizeString("Is any 3TG intentionally added or used in the material or in the production processes?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does any 3TG remain in the material?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Do any of the smelters in your supply chain source the 3TG from the covered countries?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does 100% of the 3TG (necessary to the functionality or production of Product/Material) originate from recycled or scrap sources?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("What percentage of relevant suppliers have posted a response to your supply chain survey?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Have you identified all of the smelters supplying the 3TG to your supply chain?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Do any of the smelters in your supply chain source the 3TG from conflict-affected and high-risk areas?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Has all applicable smelter information received by your company been reported in this declaration?")}
                        type={QuestionType.CmrtComplianceAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
        </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsCompanyQuestions() {
        return (
            <div>
                <h4><strong>Company Level Questions</strong></h4>
                <DynamicQuestion
                    questionText={this.localizeString("A. Have you established a responsible minerals sourcing policy?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("B. Is your responsible minerals sourcing policy publicly available on your website? (Note - If yes, the user shall specify the URL in the comment field.)")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("C. Do you require your direct suppliers to source the 3TG from smelters whose due diligence practices have been validated by an independent third party audit program?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("D. Have you implemented due diligence measures for responsible sourcing?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("E. Does your company conduct Conflict Minerals survey(s) of your relevant supplier(s)?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("F. Do you review due diligence information received from your suppliers against your company’s expectations?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("G. Does your review process include corrective action management?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("H. Is your company required to file an annual conflict minerals disclosure?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                    answerOptions={[this.localizeString("--")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
            </div>
        );
    }

    // ===============================================================================
    private renderConflictMineralsCompanyQuestionsV2() {
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data" style={{ whiteSpace: "nowrap" }}>
                    <h4><strong>{this.localizeString("Company Level Questions")}</strong></h4>
                <DynamicQuestion
                    questionText={this.localizeString("A. Have you established a responsible minerals sourcing policy?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("B. Is your responsible minerals sourcing policy publicly available on your website? (Note - If yes, the user shall specify the URL in the comment field.)")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("C. Do you require your direct suppliers to source the 3TG from smelters whose due diligence practices have been validated by an independent third party audit program?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("D. Have you implemented due diligence measures for responsible sourcing?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("E. Does your company conduct Conflict Minerals survey(s) of your relevant supplier(s)?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("F. Do you review due diligence information received from your suppliers against your company’s expectations?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("G. Does your review process include corrective action management?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
                <DynamicQuestion
                    questionText={this.localizeString("H. Is your company required to file an annual conflict minerals disclosure?")}
                    type={QuestionType.CmrtCompanyQuestionAnswer}
                        answerOptions={[this.localizeString("Choose One")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
            </div>
        </div>
        );
    }
    // ===============================================================================

    private renderConflictMineralsSmelterList() {
        return (
            <div>
                <DynamicQuestion
                    questionText={this.localizeString("Smelter List")}
                    type={QuestionType.DataTable}
                    answerOptions={[this.localizeString("Smelter ID"), this.localizeString("Metal"), this.localizeString("Smelter Name"),
                    this.localizeString("Smelter Country"), this.localizeString("Source")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
            </div>
        );
    }

    // ===============================================================================

    private renderConflictMineralsSmelterListV2() {
        return (
            <div>
                <DynamicQuestion
                    questionText={this.localizeString("Smelter List")}
                    type={QuestionType.DataTable}
                    answerOptions={[this.localizeString("Smelter ID"), this.localizeString("Metal"), this.localizeString("Smelter Name"),
                    this.localizeString("Smelter Country"), this.localizeString("Source")]}
                    appServices={this.props.appServices}>
                </DynamicQuestion>
            </div>
        );
    }

    // ===============================================================================

    private renderReachCandidateComplianceForm() {
        var applicabilityOption = this.localizeString("Regulation is applicable to this product/material")
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    <DynamicQuestion
                        questionText={this.localizeString("Is this material a REACH article?")}
                        type={QuestionType.MultiCheckbox}
                        answerOptions={[this.localizeString("Material is a REACH article")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does this material contain Substances of Very High Concern (SVHCs) above the limits within EU REACH Regulation?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No"), this.localizeString("Unknown")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Supplier Comments")}
                        type={QuestionType.Comment}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                {this.renderDefaultQuestions(applicabilityOption, applicabilityOption, this.localizeString("This reference date indicates when your response was determined."))}
            </div>
        );
    }

    // ===============================================================================

    private renderReachAuthorisationComplianceForm() {
        var applicabilityOption = this.localizeString("Regulation is applicable to this product/material");
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    <DynamicQuestion
                        questionText={this.localizeString("Does the material contain Substances on the REACH Authorisation List?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No"), this.localizeString("Unknown")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Supplier Comments")}
                        type={QuestionType.Comment}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                {this.renderDefaultQuestions(applicabilityOption, applicabilityOption, this.localizeString("This reference date indicates when your response was determined."))}
            </div>
        );
    }

    // TODO: KYLE REFCHECK
    private renderReachAuthorisationV2ComplianceForm() {
        var applicabilityOption = this.localizeString("Regulation is applicable to this product/material");
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    <DynamicQuestion
                        questionText={this.localizeString("Does the product (including any of its constituents, additives or impurities) contain substances found on the List of Substances Subject to Authorisation - Annex XIV to REACH?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        instructionText={this.localizeString("The authorisation process aims to ensure that substances of very high concern (SVHC) are progressively replaced by less dangerous substances or technologies. REACH allows companies to apply for an authorisation to continue or start using and placing substances included in the Authorisation List (Annex XIV of REACH) on the market. Please visit the **[EU REACH Resources](https://www.3eco.com/eu-reach-resources)** for additional information.")}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does the product (including any of its constituents, additives or impurities) contain substances found on the Substances restricted under REACH list - Annex XVII to REACH?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        instructionText={this.localizeString("Restrictions are normally used to limit or ban the manufacture, placing on the market (including imports) or use of a substance.A restriction may apply to any substance on its own, in a mixture or in an article, including those that do not require registration (e.g. substances manufactured or imported below one tonne per year or certain polymers). To view a list of all substances currently restricted under REACH please visit the **[EU REACH Resources](https://www.3eco.com/eu-reach-resources)**.")}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Supplier Comments")}
                        type={QuestionType.Comment}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                {this.renderDefaultQuestions(applicabilityOption, applicabilityOption, this.localizeString("This reference date indicates when your response was determined."))}
            </div>
        );
    }

    // ===============================================================================

    private renderRohsComplianceForm() {
        const rohsExemptionCodes = RohsExemptions.allCodes();
        var applicabilityOption = this.localizeString("Regulation is applicable to this product/material");
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    <DynamicQuestion
                        questionText={this.localizeString("Is this product subject to Directive 2011/65/EU on the restriction of the use of certain hazardous substances in electrical and electronic equipment (RoHS)?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        instructionText={this.localizeString("The RoHS Directive aims to prevent the risks posed to human health and the environment related to the management of electronic and electrical waste. The RoHS Directive currently restricts the use of ten substances: lead, cadmium, mercury, hexavalent chromium, polybrominated biphenyls (PBB) and polybrominated diphenyl ethers (PBDE), bis(2-ethylhexyl) phthalate (DEHP), butyl benzyl phthalate (BBP), dibutyl phthalate (DBP) and diisobutyl phthalate (DIBP). All products with an electrical and electronic component, unless specifically excluded, have to comply with these restrictions.")}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does this product meet EU RoHS Requirements?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Meets without Exemptions"), this.localizeString("Meets with Exemptions"), this.localizeString("Fails to meet Requirements"), this.localizeString("Unknown")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("If the product requires exemptions, please select them below:")}
                        type={QuestionType.MultiCheckbox}
                        answerOptions={rohsExemptionCodes.map(code => this.localizeString(RohsExemptions.getEnglishDescriptionForCode(code)))}
                        appServices={this.props.appServices}
                        instructionText={this.localizeString("Please make sure that the exemption you have provided is still valid. You can find more information here: https://environment.ec.europa.eu/topics/waste-and-recycling/rohs-directive/implementation-rohs-directive_en")}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Does this product contain any RoHS restricted substances?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        instructionText={this.localizeString("Especially needed for products that can be used as part of an electrical and electronic equipment e.g., spare parts or means for repair (screws, solders, etc.). Restricted substances: Lead (0,1%), Mercury (0,1%), Cadmium (0,01%), Hexavalent chromium (0,1%), Polybrominated biphenyls (PBB) (0,1%), Polybrominated diphenyls ethers (PBDE) (0,1%), Bis(2-ethylhexyl) phthalate (DEHP) (0,1%), Butyl benzyl phthalate (BBP) (0,1%), Dibutyl phthalate (DBP) (0,1%), and Diisobutyl phthalate (DIBP) (0,1%).")}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Supplier Comments")}
                        type={QuestionType.Comment}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>
                {this.renderDefaultQuestions(applicabilityOption, applicabilityOption, this.localizeString("This reference date indicates when your response was determined."))}
            </div>
        );
    }

    // ===============================================================================

    private renderSCIPComplianceForm() {
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    <DynamicQuestion
                        questionText={this.localizeString("Is this product considered to be a REACH article?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("No - Not an Article"), this.localizeString("Article as such or Complex Object")]}
                        appServices={this.props.appServices}
                        instructionText={this.localizeString("According to REACH Article 3(3), an article is an object which during production is given a special shape, surface or design which determines its function to a greater degree than its chemical composition. A “complex object” is an object made up of more than one article.")}
                    >
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Is this material produced or imported in the EU?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Both EU produced and imported"), this.localizeString("EU imported"), this.localizeString("EU produced"), this.localizeString("We do not produce in, or import into, the EU")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Do you have a SCIP number?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Yes"), this.localizeString("No")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("SCIP Number")}
                        type={QuestionType.Text}
                        answerOptions={[]}
                        instructionText={this.localizeString("SCIP No. format should be in the form 8-4-4-4-12 for a total of 36 characters (32 hexadecimal characters and 4 hyphens)")}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("EU REACH Primary Article Identifier")}
                        type={QuestionType.ScipArticleIdentifier}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Is this material produced or imported in the EU?")}
                        type={QuestionType.Multiple}
                        answerOptions={[this.localizeString("Both EU produced and imported"), this.localizeString("EU imported"), this.localizeString("EU produced"), this.localizeString("We do not produce in, or import into, the EU")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("EU REACH Article Category")}
                        type={QuestionType.MultipleDropdown}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                    <DynamicQuestion
                        questionText={this.localizeString("Characteristics")}
                        type={QuestionType.MultipleDropdown}
                        answerOptions={[]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion> 
                    <DynamicQuestion
                        questionText={this.localizeString("Safe Use Instructions")}
                        type={QuestionType.CheckboxAndComment}
                        answerOptions={[this.localizeString("Candidate List substances is sufficient to allow safe use of the article throughout the whole life cycle including service life, disassembly and waste recycling stage.")]}
                        appServices={this.props.appServices}>
                    </DynamicQuestion>
                </div>

            </div>
        );
    }

    // ===============================================================================

    private renderRegulation = (option: string) => {
        const dynReg = this.props.surveyMetadata.dynamicRegulations.firstOrDefault(dy => dy.regulationId === option);

        if (dynReg === null) {
            return (
                <div>
                    <p>
                        {this.localizeString("No summary available")}
                    </p>
                </div>
            );
        }

        if (dynReg.regulationId === RegulationManager.CaProp65RegulationId) {
            return this.renderCal65ComplianceForm();
        } else if (dynReg.regulationId === RegulationManager.ConflictMineralsRegulationId) {
            return this.renderConflictMineralsComplianceForm();
        }
        else if (dynReg.regulationId === RegulationManager.ConflictMineralsTemplateRegulationId  ) {
            return this.renderConflictMineralsReportingTemplate();
        }
        else if (dynReg.regulationId === RegulationManager.ConflictMineralsV2RegulationId) {
            return this.renderConflictMineralsV2ComplianceForm();
        }
        else if (dynReg.regulationId === RegulationManager.ReachSvhcCandidateRegulationId) {
            return this.renderReachCandidateComplianceForm();
        } else if (dynReg.regulationId === RegulationManager.ReachSvhcAuthorizationRegulationId) {
            return this.renderReachAuthorisationComplianceForm();
        } else if (dynReg.regulationId === RegulationManager.ReachSvhcAuthorizationV2RegulationId) {
            return this.renderReachAuthorisationV2ComplianceForm();
        } else if (dynReg.regulationId === RegulationManager.RohsRegulationId) {
            return this.renderRohsComplianceForm();
        } else if (dynReg.regulationId === RegulationManager.ScipComplianceId) {
            return this.renderSCIPComplianceForm();
        } else {
            return this.renderDynamicRegulation(dynReg.uniqueQuestions);
        }
    }

    // ===============================================================================

    private renderDynamicRegulation(questions: QuestionnaireQuestion[]) {
        return (
            <div className="survey-questions-form-preview">
                <div className="grouped-data">
                    {
                        questions.map((question, index) =>
                            <DynamicQuestion
                                key={index}
                                questionText={question.text}
                                type={question.type}
                                answerOptions={question.predefinedAnswers.map(a => a.nativeAnswer)}
                                appServices={this.props.appServices}>
                            </DynamicQuestion>
                        )
                    }
                </div>
                {this.renderDefaultQuestions()}
            </div>
        );
    }

    // ===============================================================================

    private renderDefaultQuestions(applicabilityOptionText?: string, applicabilitySelectedOption?: string, dateInstructionText?: string) {
        return (
            <div className="grouped-data">
                {this.renderSupportingDocumentation()}
                {this.renderReferenceDate(dateInstructionText)}
                {this.renderApplicability(applicabilityOptionText, applicabilitySelectedOption)}
            </div>
        )
    }

    // ===============================================================================
    private renderSupportingDocumentation() {
        return <DynamicQuestion
            questionText={this.localizeString("Please provide any supporting documentation you have")}
            type={QuestionType.File}
            answerOptions={[]}
            appServices={this.props.appServices}>
        </DynamicQuestion>
    }

    private renderReferenceDate(instructionText?: string) {
        return <DynamicQuestion
            questionText={this.localizeString("Response Reference Date")}
            type={QuestionType.Datetime}
            answerOptions={[]}
            instructionText={instructionText}
            appServices={this.props.appServices}>
        </DynamicQuestion>
    }

    private renderApplicability(optionText?: string, selectedOption?: string) {
        return <DynamicQuestion
            questionText={this.localizeString("Applicability")}
            type={QuestionType.MultiCheckbox}
            answerOptions={[optionText ?? this.localizeString("Regulation is applicable to this product/material")]}
            selectedOptions={[selectedOption]}
            appServices={this.props.appServices}>
        </DynamicQuestion>
    }

    // ===============================================================================

    private renderComplianceDataBody() {
        const regulationsToRender = RegulationManager.getRegulationsAs2GroupsAlphabatized(this.props.surveyMetadata.dynamicRegulations);
        //regulationsToRender = RegulationManager.removeExcludedRegulations(regulationsToRender);        
        
        const anyMdRequired = this.props.surveyRequest.fields.some(e => e.requiredInLeafRows || e.requiredInNonSummaryNonLeafRows || e.requiredInSummaryRow); // Md(Material Disclosure)

        const filteredRegulations = this.getFilteredRegulationsToRender(regulationsToRender);
        const regulationIdToName = this.regulationArrayToIdNameMap(filteredRegulations);

        return (
            <div className="section-options-body-content">
                {this.props.surveyRequest.dataRequested.complianceList.includes(RegulationManager.ScipComplianceId) && !this.props.surveyRequest.dataRequested.complianceList.includes(RegulationManager.ReachSvhcCandidateRegulationId) &&
                    <div className="alert alert-warning" role="alert">
                        <p>{this.localizeString("You must include REACH Candidate to include SCIP as well.")}</p>
                    </div>}
                <div className="alert alert-info">
                    <div className="title">{this.localizeString("Compliance Determinations")}</div>
                    <div className="instructions">{this.localizeString("Ask your supplier for compliance determinations that will populate in your 3E Exchange materials. Suppliers can also attach supporting documents.")}</div>
                    <div className="controls">
                        <div className="compliance-deteminations-container">
                            <div className="form-group">
                                {filteredRegulations
                                    .sort((firstName, secondName) => firstName.config.name.localeCompare(secondName.config.name))
                                    .map((value) => {
                                        return (
                                            <div key={value.regulationId} className="checkbox">
                                                <label>
                                                    <input type="checkbox" checked={this.props.surveyRequest.dataRequested.complianceList.includes(value.regulationId)} onChange={e => this.complianceRegulation_onChange(e, value.regulationId)} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.complianceList.includes(value.regulationId)} /> {value.config.displayName?.length > 0 ? value.config.displayName : value.config.name}
                                                </label>
                                            </div>
                                        );
                                    })}
                            </div>
                            <div>
                                <div title={this.localizeString("This will require a supplier to disclose any regulated substances if they answer yes to a question about their product containing said regulatory substances. This can only be selected if Material Disclosure is added.")}> 
                                    <CheckBox checked={anyMdRequired && this.props.surveyRequest.metadata.requireSuppliersToDiscloseRegulatoryIngredients} onValueChange={this.requireSupplierToAddRegulatedSubstancesCheckbox_changed} isDisabled={!this.props.surveyRequest.dataRequested.materialDisclosureAsked || !anyMdRequired || !this.isSurveyEditable()} checkboxLabelText={this.localizeString("Require Supplier to Enter Regulated Ingredients into BOM")} />
                                </div>
                            </div>
                        </div>
                        
                        {this.isSurveyEditable() &&
                            <div className="form-group">
                                <button disabled={this.props.snapshotSurveyState?.surveyRequest.dataRequested.complianceDataAsked} role="button" className={`btn ${(this.props.surveyRequest.dataRequested.complianceDataAsked ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.ComplianceData)}>{this.props.surveyRequest.dataRequested.complianceDataAsked ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>
                            </div>
                        }
                    </div>
                </div>
                <div className="alert alert-example-fields">
                    <div className="title">Preview</div>
                    <div className="controls">
                        <DynamicSidebar
                            sideBarOptions={this.getFilteredRegulationsToRender(regulationsToRender).map(r => r.regulationId)}
                            localizedOptions={this.regulationArrayToIdNameMap(this.getFilteredRegulationsToRender(regulationsToRender))}
                            selectedOptionRenderer={this.renderRegulation}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private convertSectionFieldsToFlatArray(section: Section) {
        return section.fields.selectMany(field => StandardSummaryDataFieldsManager.getFieldAndAllSubChildrenInTree(field));
    }

    // ===============================================================================

    private renderSustainabilityDataBody() {
        const standardSummaryDataFields = StandardSummaryDataFieldsManager.getConfig();

        const page = standardSummaryDataFields.pages.firstOrDefault(p => p.title === "Lifecycle Impacts"); // is this ok with localize?

        if (page === null) {
            throw new Error("Page not found");
        }

        const embodiedCarbonSection = page.sections.firstOrDefault(s => s.title === "LCA/EPD");
        const waterUseSection = page.sections.firstOrDefault(s => s.title === "Water Use");
        const transparencySection = page.sections.firstOrDefault(s => s.title === "Transparency");
        const otherSections = page.sections.filter(s => s.title === "Assessment & Commitments" || s.title === "Optimization");

        if (transparencySection === null || otherSections === null || waterUseSection === null || embodiedCarbonSection === null) {
            throw new Error("One or more sections not found.");
        }

        const embodiedCarbonFields = this.convertSectionFieldsToFlatArray(embodiedCarbonSection);
        const waterUseFields = this.convertSectionFieldsToFlatArray(waterUseSection);
        const transparencyFields = this.convertSectionFieldsToFlatArray(transparencySection);
        const otherFields = otherSections
            .selectMany(section => this.convertSectionFieldsToFlatArray(section));

        return (
            <div className="section-options-body-content">
                <div className="alert alert-info">
                    <div className="title">{this.localizeString("Lifecycle Impacts")}</div>
                    <div className="instructions">{this.localizeString("Ask your supplier for data on Water Use and Embodied Carbon")}</div>
                    <div className="controls">
                        <div className="checkbox">
                            <label>
                                <input type="checkbox" checked={this.props.surveyRequest.dataRequested.embodiedCarbonDataAsked} onChange={e => this.props.setDataRequested({ embodiedCarbonDataAsked: !this.props.surveyRequest.dataRequested.embodiedCarbonDataAsked })} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.embodiedCarbonDataAsked} /> {this.localizeString("Embodied Carbon")}
                            </label>
                        </div>
                        <div className="checkbox">
                            <label>
                                <input type="checkbox" checked={this.props.surveyRequest.dataRequested.waterUseDataAsked} onChange={e => this.props.setDataRequested({ waterUseDataAsked: !this.props.surveyRequest.dataRequested.waterUseDataAsked })} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.waterUseDataAsked} /> {this.localizeString("Water Use")}
                            </label>
                        </div>
                        <div className="checkbox">
                            <label>
                                <input type="checkbox" checked={this.props.surveyRequest.dataRequested.carbonTransparencyDataAsked} onChange={e => this.props.setDataRequested({ carbonTransparencyDataAsked: !this.props.surveyRequest.dataRequested.carbonTransparencyDataAsked })} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.carbonTransparencyDataAsked} /> {this.localizeString("Carbon Transparency")}
                            </label>
                        </div>
                        <div className="checkbox">
                            <label>
                                <input type="checkbox" checked={this.props.surveyRequest.dataRequested.carbonOtherDataAsked} onChange={e => this.props.setDataRequested({ carbonOtherDataAsked: !this.props.surveyRequest.dataRequested.carbonOtherDataAsked })} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.carbonOtherDataAsked} /> {this.localizeString("Carbon Assessment, Commitments, and Optimization")}
                            </label>
                        </div>
                        {this.isSurveyEditable() &&
                            <div className="form-group">
                                <button disabled={this.props.snapshotSurveyState?.surveyRequest.dataRequested.sustainabilityDataAsked} role="button" className={`btn ${(this.props.surveyRequest.dataRequested.sustainabilityDataAsked ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.SustainabilityData)}>{this.props.surveyRequest.dataRequested.sustainabilityDataAsked ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>
                            </div>
                        }
                    </div>
                </div>
                <div className="alert alert-example-fields">
                    <div className="title">Preview</div>
                    <div className="controls">
                        <SidebarDetail localizedSidebarNavTitles={[this.localizeString("Embodied Carbon"), this.localizeString("Water Use"), this.localizeString("Carbon Transparency"), this.localizeString("Carbon Assessment, Commitments, and Optimization")]}>
                            <CreateAndEditSurveyPreviewRenderer summaryFields={embodiedCarbonFields} />
                            <CreateAndEditSurveyPreviewRenderer summaryFields={waterUseFields} />
                            <CreateAndEditSurveyPreviewRenderer summaryFields={transparencyFields} />
                            <CreateAndEditSurveyPreviewRenderer summaryFields={otherFields} />
                        </SidebarDetail>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderCircularityDataBody() {
        return (
            <div className="section-options-body-content">
                <div className="alert alert-info" role="alert">
                    <div className="title">{this.localizeString("Circularity Data")}</div>
                    <div className="instructions">{this.localizeString("Ask your supplier for Circularity data - End of Life options, contact, & instructions.")}</div>
                    <div className="controls">
                        {this.isSurveyEditable() &&
                            <div className="form-group">
                                <button disabled={this.props.snapshotSurveyState?.surveyRequest.dataRequested.circularityDataAsked} role="button" className={`btn ${(this.props.surveyRequest.dataRequested.circularityDataAsked ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.CircularityData)}>{this.props.surveyRequest.dataRequested.circularityDataAsked ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>
                            </div>
                        }
                    </div>
                </div>
                <div className="alert alert-example-fields">
                    <div className="title">{this.localizeString("Preview")}</div>
                    <div className="controls">
                        <div className="example-controls form-inline">
                            <div>
                                <label>{this.localizeString("Packaging")}</label><select className="form-control" disabled><option>{this.localizeString("Included")}</option><option>{this.localizeString("Excluded")}</option></select>
                            </div>
                            <div>
                                <label>{this.localizeString("End of Life Instructions")}</label><input className="form-control" type="text" disabled />
                            </div>
                            <div>
                                <label>{this.localizeString("End of Life Contact")}</label><input className="form-control" type="text" disabled />
                            </div>
                            <div>
                                <label>{this.localizeString("Documents \u0026 Certifications")}</label><button className="btn btn-default" disabled><span className="glyphicon glyphicon-plus"></span> {this.localizeString("Add Document")}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderSupplierInfoBody() {
        return (
            <div className="section-options-body-content">
                <div className="alert alert-info" role="alert">
                    <div className="title">{this.localizeString("Supplier Information")}</div>
                    <div className="instructions">{this.localizeString("Send your supplier a general questionnaire that is about the supplier themselves and not about material content.")}</div>
                    <div className="controls">
                        {this.isSurveyEditable() &&
                            <div className="form-group">
                                <button role="button" className={`btn ${(false ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.SupplierInfo)}>{false ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderVocDataBody() {
        const standardSummaryDataFields = StandardSummaryDataFieldsManager.getConfig();

        const page = standardSummaryDataFields.pages.firstOrDefault(p => p.title === "VOC Data"); // is this ok with localize?

        if (page === null) {
            throw new Error("Page not found");
        }

        const transparencySection = page.sections.firstOrDefault(s => s.title === "Transparency");
        const otherSections = page.sections.filter(s => s.title !== "Transparency");

        if (transparencySection === null || otherSections === null) {
            throw new Error("One or more sections not found");
        }

        const transparencyFields = this.convertSectionFieldsToFlatArray(transparencySection);
        const otherFields = otherSections
            .selectMany(section => this.convertSectionFieldsToFlatArray(section));

        return (
            <div className="section-options-body-content">
                <div className="alert alert-info">
                    <div className="title">{this.localizeString("VOC Data")}</div>
                    <div className="instructions">{this.localizeString("Ask your supplier for data on VOCs")}</div>
                    <div className="controls">
                        <div className="checkbox">
                            <label>
                                <input type="checkbox" checked={this.props.surveyRequest.dataRequested.vocTransparencyDataAsked} onChange={e => this.props.setDataRequested({ vocTransparencyDataAsked: !this.props.surveyRequest.dataRequested.vocTransparencyDataAsked })} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.vocTransparencyDataAsked} /> {this.localizeString("Transparency")}
                            </label>
                        </div>
                        <div className="checkbox">
                            <label>
                                <input type="checkbox" checked={this.props.surveyRequest.dataRequested.vocOtherDataAsked} onChange={e => this.props.setDataRequested({ vocOtherDataAsked: !this.props.surveyRequest.dataRequested.vocOtherDataAsked })} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.vocOtherDataAsked} /> {this.localizeString("Assessment, Commitments, and Optimization")}
                            </label>
                        </div>
                        {this.isSurveyEditable() &&
                            <div className="form-group">
                                <button disabled={this.props.snapshotSurveyState?.surveyRequest.dataRequested.vocDataAsked} role="button" className={`btn ${(this.props.surveyRequest.dataRequested.vocDataAsked ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.VocData)}>{this.props.surveyRequest.dataRequested.vocDataAsked ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>
                            </div>
                        }
                    </div>
                </div>
                <div className="alert alert-example-fields">
                    <SidebarDetail localizedSidebarNavTitles={[this.localizeString("VOC Transparency"), this.localizeString("VOC Assessment, Commitments, and Optimization")]}>
                        <CreateAndEditSurveyPreviewRenderer summaryFields={transparencyFields} />
                        <CreateAndEditSurveyPreviewRenderer summaryFields={otherFields} />
                    </SidebarDetail>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderDocumentsCertificationsBody() {
        const valueToFriendlyNameMap: { [key: string]: string } = {
            ["HPD"]: "HPD",
            ["C2C"]: "C2C",
            ["VOC"]: "VOC",
            ["Other"]: this.localizeString("Other")
        };

        const otherDocuments = this.props.surveyRequest.dataRequested.documentsCertificationList.filter((v, i) => v.documentCertificationType === "Other");

        return (
            <div className="section-options-body-content">
                <div className="alert alert-info" role="alert">
                    <div className="title">{this.localizeString("Additional Documents & Certifications")}</div>
                    <div className="instructions"><p>{this.localizeString("Ask your supplier to optionally attach additional documents & certifications.")}</p><p>{this.localizeString("Note - Compliance, Sustainability & Circularity already include questions that ask for Documents & Certifications")}</p></div>
                    <div className="controls">
                        <div className="form-group">
                            {["C2C", "HPD", "VOC", "Other"].map(
                                (value, index) => {
                                    const exists = this.props.surveyRequest.dataRequested.documentsCertificationList.some((v, i) => v.documentCertificationType === value);
                                    const newValue = exists ? this.props.surveyRequest.dataRequested.documentsCertificationList.filter((v, i) => v.documentCertificationType !== value) : this.props.surveyRequest.dataRequested.documentsCertificationList.concat({ documentCertificationType: value, documentNameIfOther: "" });
                                    if (value === "Other") {
                                        return (
                                            <div key={index} className="checkbox">
                                                <label>
                                                    <input type="checkbox" checked={exists} onChange={e => this.props.setDataRequested({ documentsCertificationList: newValue })} disabled={!this.isSurveyEditable() || this.props.snapshotSurveyState?.surveyRequest.dataRequested.documentsCertificationList.some((v, i) => v.documentCertificationType === value)} /> {valueToFriendlyNameMap[value]}
                                                </label>
                                                {" "}{otherDocuments.length > 0 && <a role="button" onClick={e => this.props.setDataRequested({ documentsCertificationList: this.props.surveyRequest.dataRequested.documentsCertificationList.concat({ documentCertificationType: "Other", documentNameIfOther: "" }) })}>{"Add More"}</a>}
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={index} className="checkbox">
                                            <label>
                                                <input type="checkbox" checked={exists} onChange={e => this.props.setDataRequested({ documentsCertificationList: newValue })} disabled={!this.isSurveyEditable()} /> {valueToFriendlyNameMap[value]}
                                            </label>
                                        </div>
                                    );
                                }
                            )}
                            {otherDocuments.length > 0 &&
                                this.props.surveyRequest.dataRequested.documentsCertificationList.map((v, i) =>
                                    v.documentCertificationType === "Other" &&
                                    <div className="input-group input-document" key={i}>
                                            <input className="form-control" type="text" value={v.documentNameIfOther} onChange={e => this.props.editOtherDocuments(i, e.target.value)} placeholder={this.localizeString("Add a Custom Document/Certification")}></input>{" "}
                                        <div className="input-group-btn">
                                            <a className="btn btn-sm btn-danger" onClick={e => this.props.removeOtherDocuments(i)}><span className="glyphicon glyphicon-remove"></span></a>
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                        {this.isSurveyEditable() &&
                            <div className="form-group">
                                <button disabled={this.props.snapshotSurveyState?.surveyRequest.dataRequested.documentsAsked} role="button" className={`btn ${(this.props.surveyRequest.dataRequested.documentsAsked ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.DocumentsCertifications)}>{this.props.surveyRequest.dataRequested.documentsAsked ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderCustomQuestionsBody() {
        const linkedMaterialsExists = SurveyStateController.doLinkedMaterialsExist(this.props);

        let content: JSX.Element;

        // If the user is free and the survey has had the custom questions section added before.
        if (this.shouldShowCustomQuestionsUpsell()) {
            content = <Upsell upsellType={UpsellTypes.Pro} conversionEvent="survey-view-custom-questionnaires" renderEmbedded={true} />
        }
        else {
            content = (
                <div className="alert alert-info" role="alert">
                    <div className="title">{this.localizeString("Custom Questions")}</div>
                    <div className="instructions">{this.localizeString("Attach one of your custom Questionnaires for your supplier to fill out.")}</div>
                    <div className="controls">
                        {this.props.surveyRequest.bomType !== 2 && <div className="form-group">
                            <label>
                                {this.localizeString("Custom Material Questionnaire")}
                            </label>
                            <div className="form-inline">
                                <select id="custom-material-questionnaire-select" className="form-control" value={this.props.surveyRequest.bomQuestionnaireId !== null ? (this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.bomQuestionnaireId]?.questionnaireId ?? "") : ""} onChange={e => this.bomQuestionnaireSelector_change(e.target.value)} disabled={!this.isSurveyEditable()}>
                                    <option value="">{this.localizeString("None")}</option>
                                    {Object.keys(this.props.surveyMetadata.questionnaireTable)
                                        .filter((value) => [0, 2].includes(this.props.surveyMetadata.questionnaireTable[value].type))
                                        .map((data, ind) => (
                                            <option key={ind} value={this.props.surveyMetadata.questionnaireTable[data].questionnaireId}>{this.props.surveyMetadata.questionnaireTable[data].name === "" ? "Unnamed Questionnaire" : this.props.surveyMetadata.questionnaireTable[data].name}</option>
                                        )
                                        )
                                    }
                                </select>
                                {" "}
                                <div className="checkbox">
                                    <label>
                                        <input type="checkbox" checked={this.props.surveyRequest.preFillBomQuestionnaireFromSummaryData} onChange={e => this.props.setSurveyRequestData({ preFillBomQuestionnaireFromSummaryData: e.target.checked })} disabled={!this.isSurveyEditable()} /> {this.localizeString("Pre-fill with Material Data")}
                                    </label>
                                </div>
                            </div>
                            {(this.props.surveyRequest.bomQuestionnaireId !== null) &&
                                <div style={{ paddingLeft: "20px" }}>
                                    <div className="checkbox">
                                        <label>
                                            <input type="checkbox" checked={this.props.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer ?? false} onChange={e => this.makeBomDataRequiredOnQuestionnaireAnswer_change(e.target.checked)} disabled={!this.isSurveyEditable() || linkedMaterialsExists} /> {this.localizeString("Make BOM data required if my supplier gives a specific answer on my questionnaire.")}
                                        </label>
                                    </div>
                                    {this.props.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer &&
                                        <div className="form-group form-inline">
                                            <label>{this.localizeString("Select Page:")}</label>
                                            {" "}
                                            <select className="form-control" value={this.state.currentQuestionnairePage} onChange={e => this.setQuestionnaireAnswerThatMakesBomDataRequired(parseInt(e.target.value), 0, 0)} disabled={!this.isSurveyEditable()}>
                                                {this.state.selectableQuestionnairePages?.map((page, index) => {
                                                    return <option key={index} value={index}>{page.title}</option>;
                                                })}
                                            </select>
                                        </div>
                                    }
                                    {this.props.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer &&
                                        <div className="form-group form-inline">
                                            <label>{this.localizeString("Select Question:")}</label>
                                            {" "}
                                            <select className="form-control" value={this.state.currentQuestionnaireQuestion} onChange={e => this.setQuestionnaireAnswerThatMakesBomDataRequired(this.state.currentQuestionnairePage, parseInt(e.target.value), 0)} disabled={!this.isSurveyEditable()}>
                                                {this.state.selectableQuestionnaireQuestions?.map((question, index) => {
                                                    return <option hidden={question.predefinedAnswers.length === 0} key={index} value={index}>{question.text}</option>;
                                                })}
                                            </select>
                                        </div>
                                    }
                                    {this.props.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer &&
                                        <div className="form-group form-inline">
                                            <label>{this.localizeString("Select Answer:")}</label>
                                            {" "}
                                            <select className="form-control" value={this.state.currentQuestionnaireAnswer} onChange={e => this.setQuestionnaireAnswerThatMakesBomDataRequired(this.state.currentQuestionnairePage, this.state.currentQuestionnaireQuestion, parseInt(e.target.value))} disabled={!this.isSurveyEditable()}>
                                                {this.state.selectableQuestionnaireAnswers?.map((predefinedAnswer, index) => {
                                                    return <option key={index} value={index}>{predefinedAnswer.nativeAnswer}</option>;
                                                })}
                                            </select>
                                        </div>
                                    }
                                </div>
                            }
                        </div>}
                        <div className="form-group">
                            <label>
                                {this.localizeString("Custom Supplier-level Questionnaire")}
                            </label>
                            <div className="form-inline">
                                <select id="custom-supplier-questionnaire-select" className="form-control" value={this.props.surveyRequest.supplierQuestionnaireId !== null ? this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.supplierQuestionnaireId]?.questionnaireId : ""} onChange={e => this.props.setSurveyRequestData({ supplierQuestionnaireId: e.target.value.length === 0 ? null : e.target.value })} disabled={!this.isSurveyEditable()}>
                                    <option value="">{this.localizeString("None")}</option>
                                    {Object.keys(this.props.surveyMetadata.questionnaireTable)
                                        .filter((value) => [0, 1].includes(this.props.surveyMetadata.questionnaireTable[value].type))
                                        .map((data, ind) => (
                                            <option key={ind} value={this.props.surveyMetadata.questionnaireTable[data].questionnaireId}>{this.props.surveyMetadata.questionnaireTable[data].name === "" ? "Unnamed Questionnaire" : this.props.surveyMetadata.questionnaireTable[data].name}</option>
                                        )
                                        )
                                    }
                                </select>
                                {" "}
                                <div className="checkbox">
                                    <label>
                                        <input type="checkbox" checked={this.props.surveyRequest.preFillSupplierQuestionnaireFromSummaryData} onChange={e => this.props.setSurveyRequestData({ preFillSupplierQuestionnaireFromSummaryData: !this.props.surveyRequest.preFillSupplierQuestionnaireFromSummaryData })} disabled={!this.isSurveyEditable()} /> {this.localizeString("Pre-fill with Supplier Summary Data")}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>
                                {this.localizeString("Add a custom Attestation Questionnaire")}
                            </label>
                            <div className="form-inline">
                                <select className="form-control" value={this.props.surveyRequest.attestationQuestionnaireId !== null ? this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.attestationQuestionnaireId]?.questionnaireId : ""} onChange={e => this.props.setSurveyRequestData({ attestationQuestionnaireId: e.target.value.length === 0 ? null : e.target.value })} disabled={!this.isSurveyEditable()}>
                                    <option value="">{this.localizeString("None")}</option>
                                    {Object.keys(this.props.surveyMetadata.questionnaireTable)
                                        .filter((value, index) => this.props.surveyMetadata.questionnaireTable[value].type === 3)
                                        .map((data, ind) => (<option key={ind} value={this.props.surveyMetadata.questionnaireTable[data].questionnaireId}>{this.props.surveyMetadata.questionnaireTable[data].name === "" ? "Unnamed Questionnaire" : this.props.surveyMetadata.questionnaireTable[data].name}</option>)
                                        )}
                                </select>
                            </div>
                        </div>
                        {this.isSurveyEditable() &&
                            <div className="form-group">
                                <button disabled={this.props.snapshotSurveyState?.surveyRequest.dataRequested.customQuestionsAsked} role="button" className={`btn ${(this.props.surveyRequest.dataRequested.customQuestionsAsked ? "btn-toxnot-danger" : "btn-toxnot-primary")}`} onClick={e => this.toggleQuestion(Models.DisplaySection.CustomQuestions)}>{this.props.surveyRequest.dataRequested.customQuestionsAsked ? this.localizeString("Remove from Survey") : this.localizeString("Add to Survey")}</button>
                            </div>
                        }
                    </div>
                </div>
            );
        }

        return (
            <div className="section-options-body-content">
                {content}
            </div>
        );
    }

    // ===============================================================================

    private makeBomDataRequiredOnQuestionnaireAnswer_change = (checked: boolean) => {
        const invalid = this.isQuestionnaireSelectionInvalid(this.props.surveyRequest.bomQuestionnaireId, checked);

        if (invalid) {
            this.alertInvalidIngredientsTableSetup();
            return;
        }

        if (this.props.surveyRequest.bomQuestionnaireId === null) {
            return;
        }

        this.props.setSurveyRequestData({ makeBomDataRequiredOnQuestionnaireAnswer: checked });

        if (checked) {
            const questionIsValid = (question: Models.QuestionnaireQuestion) => ["multiple", "multi-checkbox", "multiple-dropdown"].includes(question.type) && (question.predefinedAnswers.length > 0);

            const questionnaire = this.props.surveyMetadata.questionnaireTable[this.props.surveyRequest.bomQuestionnaireId];

            const startPageIndex = questionnaire.pages.findIndex(page => page.questions.some(questionIsValid));

            if (startPageIndex === -1) {
                this.setQuestionnaireAnswerThatMakesBomDataRequired(0, 0, 0);
                return;
            }

            const startQuestionIndex = questionnaire.pages[startPageIndex].questions.findIndex(question => questionIsValid(question));

            this.setQuestionnaireAnswerThatMakesBomDataRequired(startPageIndex, startQuestionIndex, 0);
        }
        else {
            this.props.setSurveyRequestData({ questionnaireAnswerThatMakesBomDataRequired: null });
        }
    }

    // ===============================================================================

    private renderSideSectionOptionsBody(type: Models.DisplaySection) {
        switch (type) {
            case Models.DisplaySection.MaterialDisclosure:
                return this.renderMaterialDisclosureBody();
            case Models.DisplaySection.ComplianceData:
                return this.renderComplianceDataBody();
            case Models.DisplaySection.SustainabilityData:
                return this.renderSustainabilityDataBody();
            case Models.DisplaySection.CircularityData:
                return this.renderCircularityDataBody();
            case Models.DisplaySection.VocData:
                return this.renderVocDataBody();
            case Models.DisplaySection.SupplierInfo:
                return this.renderSupplierInfoBody();
            case Models.DisplaySection.DocumentsCertifications:
                return this.renderDocumentsCertificationsBody();
            case Models.DisplaySection.CustomQuestions:
                return this.renderCustomQuestionsBody();
            case Models.DisplaySection.None:
                return null;
            default:
                return null;
        }
    }

    // =================================================================================

    private isSurveyEditable() {
        return ((this.props.surveyUi.currentState === Models.SurveyMode.New) || this.props.surveyRequest.state === 4 || this.props.surveyUi.currentState === Models.SurveyMode.Template) && this.props.appServices.userContext?.isEditor;
    }

    // ===============================================================================

    private getPairedSurveyableColumnIdsToFriendlyName(columnId: string) {
        let p1: string | null = null;
        let p2: string | null = null;
        switch (columnId) {
            case "percent-pre-consumer-recycled-content":
                p1 = this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap["percent-pre-consumer-recycled-content"];
                p2 = this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap["percent-post-consumer-recycled-content"];
                if (p1 !== null && p2 != null) {
                    return p1 + ", " + p2;
                } else {
                    return null
                }
            case "wet-component-percent-by-weight":
                p1 = this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap["wet-component-percent-by-weight"];
                p2 = this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap["percent-of-wet-ingredient-in-final-product"];
                if (p1 !== null && p2 != null) {
                    return p1 + ", " + p2;
                } else {
                    return null
                }
        }
        return null;
    }

    // ===============================================================================

    private bomColumnRequiredToggleButton_click = (surveyRequestColumn: Models.SurveyRequestedBomColumn, pairedColumnId: string | null) => {
        const isCurrentlyRequiredAtAll = surveyRequestColumn.requiredInSummaryRow || surveyRequestColumn.requiredInNonSummaryNonLeafRows || surveyRequestColumn.requiredInLeafRows;

        if (isCurrentlyRequiredAtAll) {
            // set as totally optional, not required at all
            this.props.updateRequestedBomColumnRequirementState(
                surveyRequestColumn.columnId,
                {
                    requiredInSummaryRow: false,
                    requiredInLeafRows: false,
                    requiredInNonSummaryNonLeafRows: false
                });

            if (pairedColumnId !== null) {
                this.props.updateRequestedBomColumnRequirementState(
                    pairedColumnId,
                    {
                        requiredInSummaryRow: false,
                        requiredInLeafRows: false,
                        requiredInNonSummaryNonLeafRows: false
                    });
            }
        }
        else {
            // add default required flags
            this.props.updateRequestedBomColumnRequirementState(surveyRequestColumn.columnId, null);

            if (pairedColumnId !== null) {
                this.props.updateRequestedBomColumnRequirementState(pairedColumnId, null);
            }
        }
    }

    // ===============================================================================

    /**
     * Determines if a given questionnaire is valid to be selected.
     * 
     * Users can't ask for a questionnaire with ingredients tables in it AND
     * also ask for material disclosure.
     * 
     * @param targetQuestionnaireId the questionnaire to test
     * @param suppliedMakeBomDataRequiredValue if supplied, use this instead of the current state
     */
    private isQuestionnaireSelectionInvalid(targetQuestionnaireId: string | null, suppliedMakeBomDataRequiredValue?: boolean) {
        if (targetQuestionnaireId === null) {
            return false;
        }

        const questionnaire = this.props.surveyMetadata.questionnaireTable[targetQuestionnaireId];
        const questionnaireHasIngredientsTable = questionnaire?.pages.selectMany(page => page.questions).some(question => question.type === "ingredients-table");
        const bomDataRequiredByQuestionnaireAnswer = suppliedMakeBomDataRequiredValue ?? (this.props.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer ?? false);

        const invalid = questionnaireHasIngredientsTable &&
            (this.props.surveyRequest.dataRequested.customQuestionsAsked &&
                (this.props.surveyRequest.dataRequested.materialDisclosureAsked || bomDataRequiredByQuestionnaireAnswer));

        return invalid;
    }

    // ===============================================================================

    private regulationChangedSideEffects = (regulationId: string, regulationChecked: boolean) => {
        if (!this.props.surveyRequest.dataRequested.materialDisclosureAsked || !this.props.surveyRequest.dataRequested.complianceDataAsked) {
            return;
        }

        const dependentColumns = RegulationDependentBomColumnsManager.RegulationDependentBomColumns[regulationId] ?? [];

        this.updateDisabledColumns(dependentColumns.map(c => c.columnId), regulationChecked);

        dependentColumns.forEach(s => {
            this.props.toggleBomColumn(SurveyStateController.standardSurveyableColumns.filter(c => c.columnId === s.columnId)[0], !regulationChecked, {
                requiredInLeafRows: s.requiredInLeafRows,
                requiredInNonSummaryNonLeafRows: s.requiredInNonSummaryNonLeafRows,
                requiredInSummaryRow: s.requiredInSummaryRow
            })
        });
    }

    // ===============================================================================

    private updateDisabledColumns(columns: string[], disable: boolean) {
        let newdisabledRegulationDependentBomColumns = [...this.state.disabledRegulationDependentBomColumns];

        if (disable) {
            columns.map(c => {
                if (!newdisabledRegulationDependentBomColumns.includes(c)) {
                    newdisabledRegulationDependentBomColumns.push(c);
                }
            });
        } else {
            newdisabledRegulationDependentBomColumns = [...this.state.disabledRegulationDependentBomColumns.filter(c => !columns.includes(c))];
        }

        this.setState({
            disabledRegulationDependentBomColumns: newdisabledRegulationDependentBomColumns
        });
    }

    // ===============================================================================

    private bomQuestionnaireSelector_change = (value: string) => {
        const targetQuestionnaireId = value.length === 0 ? null : value;

        const invalid = this.isQuestionnaireSelectionInvalid(targetQuestionnaireId);

        if (invalid) {
            this.alertInvalidIngredientsTableSetup();
            return;
        }

        this.props.setSurveyRequestData({
            bomQuestionnaireId: targetQuestionnaireId,
            makeBomDataRequiredOnQuestionnaireAnswer: false,
            questionnaireAnswerThatMakesBomDataRequired: null
        });
    }

    // ===============================================================================

    private bomColumnSummaryRowRequiredToggleButton_click = (surveyRequestColumn: Models.SurveyRequestedBomColumn, pairedColumnId: string | null) => {
        if (SurveyStateController.isColumnDisabledInSummaryRow(surveyRequestColumn.columnId)) {
            alert(this.localizeString("This column can't be required in the Summary Row because it can't be edited in the Summary Row."));
            return;
        }

        this.props.updateRequestedBomColumnRequirementState(
            surveyRequestColumn.columnId,
            {
                requiredInSummaryRow: !surveyRequestColumn.requiredInSummaryRow,
                requiredInNonSummaryNonLeafRows: surveyRequestColumn.requiredInNonSummaryNonLeafRows,
                requiredInLeafRows: surveyRequestColumn.requiredInLeafRows
            });

        if (pairedColumnId !== null) {
            this.props.updateRequestedBomColumnRequirementState(
                pairedColumnId,
                {
                    requiredInSummaryRow: !surveyRequestColumn.requiredInSummaryRow,
                    requiredInNonSummaryNonLeafRows: surveyRequestColumn.requiredInNonSummaryNonLeafRows,
                    requiredInLeafRows: surveyRequestColumn.requiredInLeafRows
                });
        }
    }

    // ===============================================================================

    private bomColumnNonLeafRowsRequiredToggleButton_click = (surveyRequestColumn: Models.SurveyRequestedBomColumn, pairedColumnId: string | null) => {
        this.props.updateRequestedBomColumnRequirementState(
            surveyRequestColumn.columnId,
            {
                requiredInSummaryRow: surveyRequestColumn.requiredInSummaryRow,
                requiredInNonSummaryNonLeafRows: !surveyRequestColumn.requiredInNonSummaryNonLeafRows,
                requiredInLeafRows: surveyRequestColumn.requiredInLeafRows
            });

        if (pairedColumnId !== null) {
            this.props.updateRequestedBomColumnRequirementState(
                pairedColumnId,
                {
                    requiredInSummaryRow: surveyRequestColumn.requiredInSummaryRow,
                    requiredInNonSummaryNonLeafRows: !surveyRequestColumn.requiredInNonSummaryNonLeafRows,
                    requiredInLeafRows: surveyRequestColumn.requiredInLeafRows
                });
        }
    }

    // ===============================================================================

    private bomColumnLeafRowsRequiredToggleButton_click = (surveyRequestColumn: Models.SurveyRequestedBomColumn, pairedColumnId: string | null) => {
        this.props.updateRequestedBomColumnRequirementState(
            surveyRequestColumn.columnId,
            {
                requiredInSummaryRow: surveyRequestColumn.requiredInSummaryRow,
                requiredInNonSummaryNonLeafRows: surveyRequestColumn.requiredInNonSummaryNonLeafRows,
                requiredInLeafRows: !surveyRequestColumn.requiredInLeafRows
            });

        if (pairedColumnId !== null) {
            this.props.updateRequestedBomColumnRequirementState(
                pairedColumnId,
                {
                    requiredInSummaryRow: surveyRequestColumn.requiredInSummaryRow,
                    requiredInNonSummaryNonLeafRows: surveyRequestColumn.requiredInNonSummaryNonLeafRows,
                    requiredInLeafRows: !surveyRequestColumn.requiredInLeafRows
                });
        }
    }

    // ===============================================================================

    private complianceRegulation_onChange = (e: React.ChangeEvent<HTMLInputElement>, changedRegulation: string) => {
        const newComplianceList = [...this.props.surveyRequest.dataRequested.complianceList.filter(c => c !== changedRegulation)];

        if (e.target.checked) {
            newComplianceList.push(changedRegulation);
        }

        this.regulationChangedSideEffects(changedRegulation, e.target.checked);
        this.props.setDataRequested({ complianceList: newComplianceList });
    }

    // ===============================================================================

    private getDisabledRegulationDependentBomColumns = () => {
        let disabledRegulationDependentBomColumns: string[] = [];

        this.props.surveyRequest.dataRequested.complianceList.forEach(c => {
            var columns = RegulationDependentBomColumnsManager.RegulationDependentBomColumns[c] ?? [];
            var columnIds = columns.map(r => r.columnId);

            disabledRegulationDependentBomColumns = disabledRegulationDependentBomColumns.concat(columnIds);
            columns.forEach(s => {
                this.props.toggleBomColumn(SurveyStateController.standardSurveyableColumns.filter(c => c.columnId === s.columnId)[0], false, {
                    requiredInLeafRows: s.requiredInLeafRows,
                    requiredInNonSummaryNonLeafRows: s.requiredInNonSummaryNonLeafRows,
                    requiredInSummaryRow: s.requiredInSummaryRow
                });
            });
        });

        return disabledRegulationDependentBomColumns;
    }

    // ===============================================================================

    private requireSupplierToAddRegulatedSubstancesCheckbox_changed = (checked: boolean) => {
        this.props.setSurveyRequestData({
            metadata: {
                ...this.props.surveyRequest.metadata,
                requireSuppliersToDiscloseRegulatoryIngredients: checked
            }
        })
    }
}

const mapDispatchToProps = { ...SurveyStateController.actionCreators, updateSupplierRowModels: SelectSuppliersMaterialsStateController.actionCreators.updateSupplierRowModelsFromSurveyEditor };
const mapStateToProps = (state: ApplicationState) => ({ ...state.createAndEditSurveysState, appServices: state.appServices, ...state.selectSuppliersMaterialsTableState })

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CreateAndEditSurveyQuestionsPage) as any);
